import type { PostHog, PostHogConfig } from 'posthog-js';

// Augment window type for PostHog
declare global {
  interface Window {
    posthog?: PostHog;
  }
}

// posthog-js (~210 kB) is loaded on demand inside initPostHog and cached here,
// so analytics never enters the initial bundle / critical path.
let posthogInstance: PostHog | null = null;

/**
 * Initialize PostHog for React frontend
 * Includes analytics and feature flags. Session replay is intentionally
 * disabled for the authenticated application.
 */
export const initPostHog = (): void => {
  const apiKey = import.meta.env['VITE_POSTHOG_API_KEY'];
  const host = import.meta.env['VITE_POSTHOG_HOST'];

  // Skip initialization if API key not configured
  if (!apiKey || !host) {
    console.warn('[PostHog] API key or host not configured, skipping initialization');
    return;
  }

  // Check if user has opted out (GDPR compliance)
  const hasOptedOut = localStorage.getItem('posthog_opt_out') === 'true';

  if (hasOptedOut) {
    console.info('[PostHog] User has opted out, skipping initialization');
    return;
  }

  // Already initialized — avoid loading/initializing twice
  if (posthogInstance) {
    return;
  }

  // Load posthog-js on demand, then initialize. Deferring the import keeps the
  // ~210 kB analytics bundle off the critical path for first paint.
  void import('posthog-js').then(({ default: posthog }) => {
    if (posthogInstance) return;

    const config: Partial<PostHogConfig> = {
      api_host: host,
      ui_host: host, // For feature flags UI

      // ✅ GDPR Compliance
      // Respect user privacy preferences
      opt_out_capturing_by_default: false, // We handle opt-out via localStorage
      persistence: 'localStorage+cookie', // Store consent preference

      // Session replay is restricted to the public marketing website.
      // Keep this unconditional so stale consent keys cannot enable it here.
      disable_session_recording: true,

      // ✅ Privacy-first configuration
      autocapture: {
        // Only capture safe interaction classes; never capture copied text.
        dom_event_allowlist: ['click', 'change', 'submit'],
        element_allowlist: ['button', 'a', 'form', 'input', 'select', 'textarea'],
      },

      // ✅ Performance monitoring
      capture_performance: true,

      // Pageviews are captured manually from the TanStack Router integration.
      capture_pageview: false,

      // ✅ Before send hook for PII scrubbing
      before_send: (event) => {
        // Scrub sensitive data (similar to Sentry scrubbing)
        // This is a simplified version - full scrubbing should match dataScrubbing.ts
        if (event && event.properties) {
          // Remove email, phone, IBAN, license numbers from event properties
          const sensitiveKeys = ['email', 'phone', 'iban', 'licenseNumber', 'token'];
          sensitiveKeys.forEach((key) => {
            if (event.properties && event.properties[key]) {
              event.properties[key] = '[REDACTED]';
            }
          });
        }
        return event;
      },

      // ✅ Loaded callback
      loaded: (ph) => {
        if (import.meta.env.DEV) {
          // Enable debug mode in development
          ph.debug();
        }
      },
    };

    posthog.init(apiKey, config);

    posthogInstance = posthog;
    if (typeof window !== 'undefined') {
      window.posthog = posthog;
    }
  });
};

/**
 * Get PostHog instance
 * Returns null if not initialized
 */
export const getPostHog = (): PostHog | null => {
  if (posthogInstance) {
    return posthogInstance;
  }
  if (typeof window !== 'undefined' && window.posthog) {
    return window.posthog;
  }
  return null;
};

/**
 * Check if PostHog is initialized
 */
export const isPostHogInitialized = (): boolean => {
  return getPostHog() !== null;
};

/**
 * Opt out of PostHog tracking (GDPR compliance)
 */
export const optOutPostHog = () => {
  localStorage.setItem('posthog_opt_out', 'true');
  const instance = getPostHog();
  if (instance) {
    instance.opt_out_capturing();
  }
};

/**
 * Opt in to PostHog tracking (GDPR compliance)
 */
export const optInPostHog = () => {
  localStorage.setItem('posthog_opt_out', 'false');
  const instance = getPostHog();
  if (instance) {
    instance.opt_in_capturing();
  }
};

/**
 * Check if user has opted out
 */
export const hasOptedOutPostHog = (): boolean => {
  return localStorage.getItem('posthog_opt_out') === 'true';
};

/**
 * Reset PostHog user data (on logout)
 */
export const resetPostHog = () => {
  const instance = getPostHog();
  if (instance) {
    instance.reset();
  }
};
